<template>
    <div>
        <div class="landing__main p--0">
            <div class="landing__main__bg relative" :class="[isNoisyBg ? 'bg-noisy': 'bg-main']">
                <div ref="navRef" class="landing__header" :class="{ 'sticky': isSticky }">
                    <LandingHeader page-type="home" />
                </div>
                
                <div class="landing__main__bg__inner landing-px-2">
                    <div id="hero" class="landing__hero__wrap landing__section">
                        <div class="landing__hero__wrap__text landing__hero__fadeFirst">
                            <h1 class="landing__hero__title text--heading text--7xl">
                                Juggle less tools. Work smarter, not scattered. 
                            </h1>
                            <p class="landing__hero__text">
                                Your clients, projects, and tools, all in sync. Elevate your workflows from proposal to payment, without switching tools or losing focus.
                            </p>
                        </div>

                        <div class="landing__hero__fadeIn">
                            <div class="landing__hero__btns">
                                <BaseButton 
                                    label="Sign up for free" 
                                    size="md"
                                    shadow
                                    @submit="handleGetStarted('free')"
                                />
                                <BaseButton 
                                    label="Book a demo call"
                                    size="md"
                                    variant="outline"
                                    variant-style="primary"
                                    class="ml--10" 
                                    shadow
                                    @submit="bookDemoCall"
                                />
                            </div>
    
                            <div class="landing__hero__img">
                                <img class="img__responsive" src="https://res.cloudinary.com/gracy126/image/upload/v1755686908/Ziner/clients_screen.svg" alt="hero image">
                            </div>
                        </div>
                    </div>
                </div>

                <div id="features" class="landing__section observer-fade">
                    <div class="landing__hero__wrap__text landing__hero__fadeFirst pl--0 pr--0">
                        <h1 class="landing__hero__title text--heading text--6xl">
                            Built for creative professionals.
                        </h1>
                        <p class="landing__hero__text mt--10">
                            Ziner offers a simplified system of features and integrations designed to help you grow as a creative.
                        </p>
                    </div>
                            
                    <LandingFeatures @open-signup="handleGetStarted('free')" />
                </div>

                <div id="integrations" class="landing__integrations landing__section observer-fade">
                    <div class="landing__header__text">
                        <h1>
                            User-friendly integrations
                        </h1>
                        <p>
                            Connect to your favourite everyday tools. Manage client meetings, add new leads through your website form, or simply track time spent on projects.
                        </p>
                    </div>
                    <div class="landing__integrations__btn">
                        <BaseButton 
                            label="Get started for free →" 
                            class="mt--40"
                            size="md"
                            shadow
                            @submit="handleGetStarted('free')"
                        />
                    </div>

                    <ul class="landing__integrations__card__list">
                        <li class="landing__integrations__card__item">
                            <div class="landing__integrations__card__inner">
                                <div class="img-container">
                                    <div class="grid-container">
                                        <div class="grid-item-outer">
                                            <div class="grid-item plus-icon" tabindex="0" role="button" aria-label="Add item">
                                                <IconSvg name="add" fill="#C2B7FA" />
                                            </div>
                                        </div>
                                        <div class="grid-item-outer">
                                            <div class="grid-item two icon" tabindex="0" role="button" aria-label="Grid item">
                                                <img src="@/assets/img/outlook.svg" alt="outlook icon">
                                            </div>
                                        </div>
                                        <div class="grid-item-outer">
                                            <div class="grid-item plus-icon" tabindex="0" role="button" aria-label="Add item">
                                                <IconSvg name="add" fill="#C2B7FA" />
                                            </div>
                                        </div>
                                        <div class="grid-item-outer">
                                            <div class="grid-item icon four" tabindex="0" role="button" aria-label="Grid item">
                                                <img src="@/assets/img/google-calendar.svg" alt="google calendar icon">
                                            </div>
                                        </div>
                                        <div class="grid-item-circle" tabindex="0" role="button" aria-label="Center item">
                                            <div class="grid-item-circle-inner flex align-items-center justify-content-center">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19"
                                                    fill="none"
                                                >
                                                    <circle cx="5.27778" cy="5.27772" r="5.27778" fill="#289CA9" />
                                                    <circle cx="14.4262" cy="5.27778" r="5.27778" fill="#289CA9" />
                                                    <circle cx="14.4262" cy="13.7222" r="5.27778" fill="#289CA9" />
                                                    <circle cx="5.27778" cy="13.7222" r="5.27778" fill="#289CA9" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="grid-item-outer">
                                            <div class="grid-item icon six" tabindex="0" role="button" aria-label="Grid item">
                                                <img src="@/assets/img/calendly.svg" alt="calendly icon">
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="text"> 
                                    <div class="flex w--fit justify-content-start badge align-items-center">
                                        <span class="mr--3">
                                            <IconSvg name="calendar" fill="#C2B7FA" />
                                        </span>
                                        <span class="">
                                            Calendar
                                        </span>  
                                    </div>
                                    
                                    <h1 class="title">
                                        Calendar management
                                    </h1>
                                    <p class="subtitle">
                                        Sync your workflows and organize your work schedules effectively.
                                    </p>
                                </div>
                            </div>
                        </li>

                        <li class="landing__integrations__card__item">
                            <div class="landing__integrations__card__inner">
                                <div class="img-container">
                                    <div class="grid-container one">
                                        <div v-for="item in integrationTypes[0]" :key="item.id" class="grid-item-outer pro">
                                            <div class="grid-item grid-item-pro" tabindex="0" role="button" aria-label="Add item">
                                                <IconSvg :name="item.iconName" fill="#C2B7FA" width="24px" height="24px" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="grid-container m-0">
                                        <div v-for="item in integrationTypes[1]" :key="item.id" class="grid-item-outer pro">
                                            <div class="grid-item grid-item-pro" tabindex="0" role="button" aria-label="Add item">
                                                <IconSvg :name="item.iconName" fill="#C2B7FA" width="24px" height="24px" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="text">  
                                    <div class="flex w--fit justify-content-start badge align-items-center">
                                        <span class="mr--3">
                                            <IconSvg name="grid" fill="#C2B7FA" />
                                        </span>
                                        <span class="">
                                            Productivity
                                        </span>  
                                    </div>
                                    <h1 class="title">
                                        Boost your productivity
                                    </h1>
                                    <p class="subtitle">
                                        Automate repititive tasks, time tracking, data collection, and more.
                                    </p>
                                </div>
                            </div>
                        </li>

                        <li class="landing__integrations__card__item">
                            <div class="landing__integrations__card__inner">
                                <div class="img-container">
                                    <div class="grid-container-img">
                                        <div style="margin-bottom: 1rem;">
                                            <img class="w-100 h--100" src="@/assets/img/Group_198.svg" alt="integrations icon">
                                        </div>
                                        <div>
                                            <img class="w-100 h--100" src="@/assets/img/Group_219.svg" alt="integrations icon">
                                        </div>
                                    </div>
                                </div>

                                <div class="text"> 
                                    <div class="flex w--fit justify-content-start badge align-items-center">
                                        <span class="mr--3">
                                            <IconSvg name="layer" fill="#C2B7FA" />
                                        </span>
                                        <span class="">
                                            AI tools: coming soon
                                        </span>  
                                    </div>
                                    <h1 class="title">
                                        Smarter workflows with AI
                                    </h1>
                                    <p class="subtitle">
                                        Use this feature for a smoother, more productive experience.
                                    </p>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <div id="pricing" ref="pricingRef" class="landing__pricing landing__section">
                    <div class="landing__pricing__text">
                        <h1 class="text--heading text--6xl">
                            Choose a plan that matches your needs
                        </h1>
                    </div>

                    <div class="landing__triple__cards">
                        <Plans 
                            :currency="currency"
                            @set-plan="handleGetStarted" 
                        />
                    </div>
                </div>

                <div id="faqs" class="landing__faqs landing__section observer-fade">
                    <div>
                        <h1 class="landing__header__text__h1 text--center">
                            Frequently Asked Questions
                        </h1>
                    </div>
                    
                    <div class="landing__faqs__wrap">
                        <ul class="landing__faqs__list">
                            <li v-for="(item, index) in faqs" :key="index" class="landing__faqs__list__item">
                                <button  
                                    :ref="`ref-${index}`" :class="{ 'active': index === currentPanelIndex && panelStyle[index]?.maxHeight }" 
                                    @click="togglePanel(index)"
                                >
                                    <span>{{ item.question }}</span>
                                </button>
                                <div 
                                    class="landing__faqs__list__item__panel" 
                                    :class="{ 'active py-4': index === currentPanelIndex && panelStyle[index]?.maxHeight }"
                                    :style="panelStyle[index]"
                                >
                                    <p>{{ item.answer }}</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div id="contact" class="landing__contact landing__section observer-fade">
                    <div class="landing__contact__wrap">
                        <div class="landing__contact__text flex-1 flex align-items-center">
                            <h1 class="text--heading text--5xl">
                                Still need help? Contact our team.
                            </h1>
                        </div>

                        <div class="l_s_w_r">
                            <SupportForm @on-complete="onComplete" />
                        </div>
                    </div>
                </div>

                <LandingFooter id="footer" :is-noisy-bg="isNoisyBg" />
            </div>
            <div />
        </div>
    </div>
</template>

<script>
import { BaseButton, Plans, IconSvg } from "@/components/ui/index";
import { faqs, pricingInfoData } from "@/utils/dummy";
import LandingFooter from "@/components/home/LandingFooter.vue";
import LandingHeader from "@/components/home/LandingHeader.vue";
import LandingFeatures from "@/components/home/Features.vue";
import SupportForm from "@/components/home/support/SupportForm.vue";
import { allCountries } from "@/plugins/countries";
import store from "@/store";

export default {
    name: "LandingPage",

    components: {
        BaseButton, 
        LandingFooter,
        LandingHeader,
        Plans,
        LandingFeatures,
        SupportForm,
        IconSvg,
    },
    
    data() {
        return {
            isNoisyBg: false,
            pricingInfoData,
            currentPanelIndex: undefined,
            faqs,
            panelStyle: {},
            integrationTypes: [
                [
                    { id: "tasks", iconName: "task" },
                    { id: "timeTracking", iconName: "time" },
                    { id: "recordings", iconName: "play-circle" },
                ],
                [
                    { id: "forms", iconName: "user-badge" },
                    { id: "pdfs", iconName: "file-doc" },
                    { id: "organization", iconName: "folder" },
                ],
            ],
            priceObserver: null,
            isSticky: false,
            allCountries,
        };
    },

    computed: {
        landingPageSectionId() {
            return this.$store.getters["app/landingPageSectionId"];
        },

        currency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        systemPlans() {
            return this.$store.getters["config/systemPlans"];
        },
    },

    watch: {
        landingPageSectionId(newVal) {            
            if (newVal) {
                this.scrollToSection(newVal);
            }
        },
    },

    async mounted() {
        if ("geolocation" in navigator) {
            await this.fetchGeoLocationData();
        }

        this.initPageIntersectionObserver();
        window.addEventListener("scroll", this.handleNavScroll);
        await this.fetchSystemPlans();
    },

    beforeUnmount() {
        window.removeEventListener("scroll", this.handleNavScroll);
    },

    methods: {
        bookDemoCall() {
            window.open("https://calendly.com/uzoamakaanyanwu26/test-event", "_blank");
        },

        goToPageSection(val) {
            this.$store.dispatch("app/setLandingPageSectionId", val);
        },

        scrollToSection(sectionId) {
            const element = document.getElementById(sectionId);            
            if (element) {
                element.scrollIntoView({ 
                    behavior: "smooth",
                    block: "start",
                });
            }
        },

        handleGetStarted(accountPlan) {
            if (!accountPlan || typeof accountPlan !== "string") return;
            
            this.$store.dispatch("app/setSelectedPlan", accountPlan?.toLowerCase());
            this.$router.push("/auth/signup");
        },

        computePricingAction(accountPlan) {
            if (accountPlan === "free") {
                return this.$router.push("/auth/signup");
            }

            localStorage.setItem("z_pricing_plan", accountPlan);
            localStorage.setItem("z_pricing_plan_currency", "NGN");
            localStorage.setItem("z_pricing_plan_active", "NGN");
            return this.$router.push("/auth/signup");
        },

        togglePanel(index) {
            this.currentPanelIndex = index;
            const elementRef = this.$refs[`ref-${index}`][0];
            const heightMultiplier = (100 * index);
            
            if (elementRef) {
                const panel = elementRef?.nextElementSibling;

                if (panel) {   
                    if (this.panelStyle[index] && this.panelStyle[index].maxHeight) {
                        this.panelStyle[index].maxHeight = null;
                    } else {
                        this.panelStyle[index] = {};
                        this.panelStyle[index].maxHeight = panel.scrollHeight + heightMultiplier + "px";
                    }
                }
            };
        },

        onComplete() {},

        async fetchGeoLocationData() {
            try {                
                const storedCurrency = localStorage.getItem("z_ip_currency");
                
                if (storedCurrency === this.currency) return; // no need because currency in vuex store === local storage currency

                if (storedCurrency) {
                    this.$store.dispatch("settings/setCurrency", { currency: storedCurrency });
                    return;
                }

                const coords = await this.getUserLocationNative();
                const countryCode = await this.getCountryFromCoords(coords.latitude, coords.longitude);
                const currency = this.allCountries.find(c => c?.code === countryCode)?.currency || "USD";

                this.$store.dispatch("settings/setCurrency", { currency });
                localStorage.setItem("z_ip_currency", currency);
            } catch (error) {
                console.log("fetchGeoLocationDataError", error);
            }
        },

        async fetchSystemPlans() {
            try {   
                if (this.systemPlans.length) return;

                await this.$store.dispatch("config/fetchSystemPlans");
            } catch (error) {
                console.log("fetchGeoLocationDataError", error);
            }
        },

        initPageIntersectionObserver() {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: "0px 0px -50px 0px",
            });

            // Observe all elements with observer-fade class
            document.querySelectorAll(".observer-fade").forEach(el => {                
                observer.observe(el);
            });
        },

        handleNavScroll(evt) {
            if (this.$refs.navRef) {                
                const headerRect = this.$refs?.navRef?.getBoundingClientRect();                
                this.isSticky = headerRect.top <= 0;
            }
        },

        getUserLocationNative() {
            return new Promise((resolve, reject) => {
                if (!navigator.geolocation) {
                    reject(new Error("Geolocation not supported"));
                    return;
                }
                
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        });
                    },
                    (error) => {
                        reject(error);
                    },
                );
            });
        },

        async getCountryFromCoords(lat, lon) {
            const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`,
            );
            const data = await response.json();
            return data.countryCode;
        },
    },
};
</script>